import {
  faCircleStop,
  faEllipsisVertical,
  faEye,
  faPencil,
  faPeopleArrows,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, memo } from 'react';

import type { Props } from '@/components/TreatmentGroupActions/types';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip
} from '@/ui';

const TreatmentGroupActions: FC<Props> = ({
  canDelete = true,
  canEdit = true,
  canEnd = true,
  canMoveMembers = true,
  canView = true,
  group,
  onDelete,
  onEdit,
  onEnd,
  onMoveMembers,
  onView,
  variant = 'inline'
}) => {
  const showAddMembers = canMoveMembers && !!onMoveMembers;
  const showEnd = canEnd && !group.end_date && !group.is_draft;

  const viewButton = canView && onView && (
    <Tooltip content="View">
      <Button
        type="button"
        aria-label="View"
        color="neutral"
        variant="outline"
        size="icon-md"
        onClick={() => onView(group)}>
        <FontAwesomeIcon icon={faEye} />
      </Button>
    </Tooltip>
  );

  const editButton = canEdit && (
    <Tooltip content="Edit">
      <Button
        type="button"
        aria-label="Edit"
        color="neutral"
        variant="outline"
        size="icon-md"
        onClick={() => onEdit(group)}>
        <FontAwesomeIcon icon={faPencil} />
      </Button>
    </Tooltip>
  );

  if (variant === 'compact') {
    const hasOverflowActions = showAddMembers || showEnd || canDelete;
    return (
      <div className="flex h-full items-center gap-3">
        {viewButton}
        {editButton}
        {hasOverflowActions && (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Tooltip content="More actions">
                <Button
                  type="button"
                  aria-label="More actions"
                  color="neutral"
                  variant="outline"
                  size="icon-md">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </Button>
              </Tooltip>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="end">
              {showAddMembers && onMoveMembers && (
                <DropdownMenuItem icon={faPeopleArrows} onClick={() => onMoveMembers(group)}>
                  Add members to another group
                </DropdownMenuItem>
              )}
              {showEnd && (
                <DropdownMenuItem icon={faCircleStop} onClick={() => onEnd(group)}>
                  End
                </DropdownMenuItem>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    icon={faTrash}
                    color="destructive"
                    onClick={() => onDelete(group)}>
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full items-center gap-3">
      {viewButton}
      {showAddMembers && onMoveMembers && (
        <Tooltip content="Add members to another group">
          <Button
            type="button"
            aria-label="Add members to another group"
            color="neutral"
            variant="outline"
            size="icon-md"
            onClick={() => onMoveMembers(group)}>
            <FontAwesomeIcon icon={faPeopleArrows} />
          </Button>
        </Tooltip>
      )}
      {showEnd && (
        <Tooltip content="End">
          <Button
            type="button"
            aria-label="End"
            color="neutral"
            variant="outline"
            size="icon-md"
            onClick={() => onEnd(group)}>
            <FontAwesomeIcon icon={faCircleStop} />
          </Button>
        </Tooltip>
      )}
      {editButton}
      {canDelete && (
        <Tooltip content="Delete">
          <Button
            type="button"
            aria-label="Delete"
            color="destructive"
            variant="outline"
            size="icon-md"
            onClick={() => onDelete(group)}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export default memo(TreatmentGroupActions);
